import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import DateTimeUtil from "../../utils/dateTime";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { GoogleMap, useJsApiLoader, Marker, Polyline, Polygon } from "@react-google-maps/api";
import { RootState, useAppDispatch } from "../../redux/store";
import { fetchSessionById, fetchLocationPoints, clearSelectedSession } from "../../redux/features/trackingSessions";
import { getEmployees, getSites } from "../../redux/features/common";
import { TrackingSession, LocationPoint } from "../../types/model/trackingSessionTypes";

// ─── Haversine distance (metres between two lat/lng) ─────────────────────────
function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function totalDistanceKm(points: LocationPoint[]): number {
  let d = 0;
  for (let i = 1; i < points.length; i++) {
    d += haversineM(points[i - 1].latitude, points[i - 1].longitude, points[i].latitude, points[i].longitude);
  }
  return d / 1000;
}

// ─── Formatting helpers ────────────────────────────────────────────────────────
function formatDuration(startedAt: string, endedAt?: string): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const mins = Math.floor(Math.max(0, end - start) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtServerTime(ts?: string): string {
  if (!ts) return "—";
  return DateTimeUtil.formatWithDayjs(ts, "MMM D, YYYY hh:mm:ss A") + " (server)";
}

function fmtPointTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_STYLES: Record<TrackingSession["status"], string> = {
  active: "bg-green-100 text-green-700 border border-green-300",
  paused: "bg-orange-100 text-orange-700 border border-orange-300",
  stopped: "bg-green-100 text-green-700 border border-green-300",
  auto_stopped: "bg-red-100 text-red-700 border border-red-300",
};

// ─── Android / iOS icons ──────────────────────────────────────────────────────
const AndroidIcon = () => (
  <svg className="w-4 h-4 text-green-600 inline mr-1" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zm-2.5-1C2.67 17 2 17.67 2 18.5v-9C2 8.67 2.67 8 3.5 8S5 8.67 5 9.5v9c0 .83-.67 1.5-1.5 1.5zm17 0c-.83 0-1.5-.67-1.5-1.5v-9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5zM15.53 2.16l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A5.84 5.84 0 0012 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C7.15 3.23 6 5.01 6 7h12c0-1.99-1.15-3.77-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
  </svg>
);

const IOSIcon = () => (
  <svg className="w-4 h-4 text-gray-600 inline mr-1" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

// ─── Collapsible section ──────────────────────────────────────────────────────
const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title, subtitle, children,
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-t border-gray-100 mt-4">
      <button
        className="w-full flex items-center justify-between py-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-semibold text-gray-800 text-sm">
          {title}{" "}
          {subtitle && <span className="text-gray-400 font-normal">{subtitle}</span>}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>
      {open && children}
    </div>
  );
};

// ─── Field row ────────────────────────────────────────────────────────────────
const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start py-1.5">
    <span className="text-gray-400 text-sm w-44 shrink-0">{label}</span>
    <span className="text-gray-800 text-sm font-medium flex-1">{value}</span>
  </div>
);

// ─── Map container style ──────────────────────────────────────────────────────
const MAP_CONTAINER = { width: "100%", height: "500px" };

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

// ─── Main component ───────────────────────────────────────────────────────────
const SessionDetailsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isLoaded: mapsLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  const { selectedSession: session, locationPoints, detailLoading, pointsLoading, error } =
    useSelector((state: RootState) => state.trackingSessions);
  const employeeList = useSelector((state: RootState) => state.common.employeeList);
  const sites = useSelector((state: RootState) => state.common.sites);

  const [activeTab, setActiveTab] = useState<"map" | "timeline">("map");
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    dispatch(getEmployees({ l_i_m_i_t: "500" }));
    dispatch(getSites());
  }, [dispatch]);

  useEffect(() => {
    if (sessionId) {
      dispatch(fetchSessionById(sessionId));
      dispatch(fetchLocationPoints(sessionId));
    }
    return () => { dispatch(clearSelectedSession()); };
  }, [dispatch, sessionId]);

  const emp = useMemo(
    () => session ? employeeList.find((e) => e.employee_id === session.os_emp_account_id) : undefined,
    [employeeList, session]
  );
  const displayName = emp ? emp.full_name : session?.os_emp_account_id ?? "";

  // Fit map to GPS track only — fences are drawn as overlays wherever they are
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (!locationPoints.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    locationPoints.forEach((p) => bounds.extend({ lat: p.latitude, lng: p.longitude }));
    map.fitBounds(bounds, 60);
  }, [locationPoints]);

  // Re-fit when GPS points arrive after the map is already mounted
  useEffect(() => {
    if (!mapRef.current || !mapsLoaded || !locationPoints.length) return;
    onMapLoad(mapRef.current);
  }, [locationPoints, mapsLoaded, onMapLoad]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!locationPoints.length) return { distKm: 0, pointCount: 0, avgAccuracy: 0, maxSpeedKmh: 0 };
    const distKm = totalDistanceKm(locationPoints);
    const avgAccuracy =
      locationPoints.filter((p) => p.accuracy != null).reduce((s, p) => s + (p.accuracy ?? 0), 0) /
      (locationPoints.filter((p) => p.accuracy != null).length || 1);
    const maxSpeedMs = Math.max(...locationPoints.map((p) => p.speed ?? 0));
    return {
      distKm: +distKm.toFixed(1),
      pointCount: locationPoints.length,
      avgAccuracy: Math.round(avgAccuracy),
      maxSpeedKmh: Math.round(maxSpeedMs * 3.6),
    };
  }, [locationPoints]);

  // ── Map data ───────────────────────────────────────────────────────────────
  const mapPath = useMemo(
    () => locationPoints.map((p) => ({ lat: p.latitude, lng: p.longitude })),
    [locationPoints]
  );
  const mapCenter = useMemo(() => {
    if (!mapPath.length) return { lat: 0, lng: 0 };
    return mapPath[Math.floor(mapPath.length / 2)];
  }, [mapPath]);

  // ── Loading / error states ─────────────────────────────────────────────────
  if (detailLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{error}</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 text-sm hover:underline">← Go back</button>
      </div>
    );
  }

  const getInitials = (id: string) =>
    id.split("-").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 pt-6 pb-4">
          <h1 className="text-xl font-bold text-gray-900">Session Details</h1>
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {session ? (
          <div className="bg-white shadow-sm border border-gray-100 overflow-hidden">
            {/* ── Employee row ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                {emp?.profile_image_url ? (
                  <img
                    src={emp.profile_image_url}
                    alt={displayName}
                    className="w-12 h-12 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-base font-bold shrink-0">
                    {getInitials(displayName)}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900 text-base">{displayName}</p>
                  <p className="text-sm text-gray-400">{session.os_emp_account_id}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[session.status]}`}>
                  {session.status.replace("_", " ")}
                </span>
                {session.stop_reason && (
                  <p className="text-xs text-gray-400 mt-1">{session.stop_reason}</p>
                )}
              </div>
            </div>

            {/* ── Date / time / duration row ────────────────────────────────── */}
            <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
              <div className="px-4 py-3">
                <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs">Date</span>
                </div>
                <p className="font-semibold text-gray-800 text-sm">{fmtDate(session.startedAt)}</p>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs">startedAt</span>
                </div>
                <p className="font-semibold text-gray-800 text-sm">{fmtTime(session.startedAt)}</p>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs">endedAt</span>
                </div>
                <p className="font-semibold text-gray-800 text-sm">{fmtTime(session.endedAt)}</p>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs">Duration</span>
                </div>
                <p className="font-semibold text-gray-800 text-sm">
                  {formatDuration(session.startedAt, session.endedAt)}
                </p>
              </div>
            </div>

            {/* ── Reason / stop_reason / site / platform row ────────────────── */}
            <div className="grid grid-cols-4 gap-4 px-5 py-4 border-b border-gray-100">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">reason</p>
                <p className="text-sm font-semibold text-gray-800">{session.reason ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">stop_reason</p>
                <p className="text-sm font-semibold text-gray-800">{session.stop_reason ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">site_id</p>
                <p className="text-sm font-semibold text-gray-800">{session.site_id ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">device_platform</p>
                <p className="text-sm font-semibold text-gray-800 flex items-center">
                  {session.device_platform === "android" ? <AndroidIcon /> : session.device_platform === "ios" ? <IOSIcon /> : null}
                  {session.device_platform ?? "—"}
                </p>
              </div>
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────────── */}
            <div className="flex border-b border-gray-100 px-5">
              {(["map", "timeline"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 mr-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {tab === "map" ? "Map (location_tracking)" : "Timeline"}
                </button>
              ))}
            </div>

            {/* ── Map tab ───────────────────────────────────────────────────── */}
            {activeTab === "map" && (
              <>
                <div className="relative">
                  {pointsLoading ? (
                    <div className="flex items-center justify-center bg-gray-100" style={{ height: 500 }}>
                      <div className="w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : mapPath.length === 0 ? (
                    <div className="flex flex-col items-center justify-center bg-gray-50 gap-2 text-gray-400" style={{ height: 500 }}>
                      <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <p className="text-sm">No location data for this session.</p>
                    </div>
                  ) : !mapsLoaded ? (
                    <div className="flex items-center justify-center bg-gray-100" style={{ height: 500 }}>
                      <div className="w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <GoogleMap
                      mapContainerStyle={MAP_CONTAINER}
                      center={mapCenter}
                      zoom={14}
                      onLoad={onMapLoad}
                      options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                    >
                      {/* ── Geofence polygons ── */}
                      {sites.map((site) =>
                        site.geo_fence?.map((fence, fi) => {
                          const paths = (fence.paths ?? []).filter((p) => p.lat && p.lng) as { lat: number; lng: number }[];
                          if (paths.length < 3) return null;
                          return (
                            <Polygon
                              key={`${site.us_user_site_profile_id}-${fi}`}
                              paths={paths}
                              options={{
                                strokeColor: "#2563EB",
                                strokeOpacity: 0.8,
                                strokeWeight: 2,
                                fillColor: "#2563EB",
                                fillOpacity: 0.1,
                              }}
                            />
                          );
                        })
                      )}

                      {/* ── GPS path ── */}
                      <Polyline
                        path={mapPath}
                        options={{ strokeColor: "#2563EB", strokeWeight: 3, strokeOpacity: 0.9 }}
                      />
                      <Marker
                        position={mapPath[0]}
                        label={{ text: "S", color: "white", fontWeight: "bold", fontSize: "11px" }}
                        icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#16a34a", fillOpacity: 1, strokeColor: "white", strokeWeight: 2 }}
                      />
                      <Marker
                        position={mapPath[mapPath.length - 1]}
                        label={{ text: "E", color: "white", fontWeight: "bold", fontSize: "11px" }}
                        icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#dc2626", fillOpacity: 1, strokeColor: "white", strokeWeight: 2 }}
                      />
                      {mapPath.slice(1, -1).map((pos, i) => (
                        <Marker
                          key={i}
                          position={pos}
                          icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: "#ffffff", fillOpacity: 1, strokeColor: "#2563EB", strokeWeight: 2 }}
                        />
                      ))}
                    </GoogleMap>
                  )}
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-4 divide-x divide-gray-100 border-t border-gray-100">
                  {[
                    { label: "Total Distance", value: `${stats.distKm} km` },
                    { label: "Points (location_tracking)", value: stats.pointCount },
                    { label: "Avg. Accuracy", value: `${stats.avgAccuracy} m` },
                    { label: "Max Speed", value: `${stats.maxSpeedKmh} km/h` },
                  ].map(({ label, value }) => (
                    <div key={label} className="px-4 py-3">
                      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                      <p className="text-lg font-bold text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── Timeline tab ──────────────────────────────────────────────── */}
            {activeTab === "timeline" && (
              <div className="px-5 py-4 max-h-80 overflow-y-auto">
                {pointsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : locationPoints.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-8">No location points recorded.</p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-[7px] top-0 bottom-0 w-px bg-gray-200" />
                    {locationPoints.map((pt, idx) => (
                      <div key={pt.id ?? idx} className="flex gap-4 mb-4 relative">
                        <div className={`w-3.5 h-3.5 rounded-full shrink-0 mt-0.5 border-2 border-white shadow-sm z-10 ${
                          idx === 0 ? "bg-green-500" : idx === locationPoints.length - 1 ? "bg-red-500" : "bg-blue-400"
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">{fmtPointTime(pt.timestamp)}</p>
                          <p className="text-xs text-gray-400">
                            {pt.latitude.toFixed(6)}, {pt.longitude.toFixed(6)}
                            {pt.accuracy != null ? ` · ±${pt.accuracy.toFixed(0)}m` : ""}
                            {pt.speed != null ? ` · ${(pt.speed * 3.6).toFixed(1)} km/h` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Tracking Session section ──────────────────────────────────── */}
            <div className="px-5">
              <Section title="Tracking Session" subtitle="(tracking_sessions)">
                <div className="pb-2">
                  <FieldRow label="sessionId (punch_uuid)" value={<span className="font-mono text-xs break-all">{session.sessionId}</span>} />
                  <FieldRow label="employeeId" value={session.employeeId} />
                  <FieldRow label="os_emp_account_id" value={session.os_emp_account_id} />
                  {session.user_account_id && <FieldRow label="user_account_id" value={session.user_account_id} />}
                  {session.raw_id != null && <FieldRow label="raw_id" value={String(session.raw_id)} />}
                  {session.site_id && <FieldRow label="site_id" value={session.site_id} />}
                  <FieldRow label="startedAt" value={session.startedAt} />
                  <FieldRow label="endedAt" value={session.endedAt ?? "—"} />
                  <FieldRow label="reason" value={session.reason ?? "—"} />
                  <FieldRow
                    label="status"
                    value={
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[session.status]}`}>
                        {session.status.replace("_", " ")}
                      </span>
                    }
                  />
                  <FieldRow label="stop_reason" value={session.stop_reason ?? "—"} />
                  <FieldRow label="created_at" value={fmtServerTime(session.created_at)} />
                  <FieldRow label="updated_at" value={fmtServerTime(session.updated_at)} />
                  <FieldRow
                    label="device_platform"
                    value={
                      <span className="flex items-center">
                        {session.device_platform === "android" ? <AndroidIcon /> : session.device_platform === "ios" ? <IOSIcon /> : null}
                        {session.device_platform ?? "—"}
                      </span>
                    }
                  />
                </div>
              </Section>

              {/* ── Sample Location Point section ───────────────────────────── */}
              {locationPoints.length > 0 && (
                <Section title="Sample Location Point" subtitle="(location_tracking)">
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 overflow-x-auto">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                      {JSON.stringify(
                        (() => {
                          const p = locationPoints[0];
                          const { id, synced_at, ...rest } = p;
                          return rest;
                        })(),
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </Section>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl mx-4 p-8 text-center text-gray-400 shadow-sm border border-gray-100">
            Session not found.
          </div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
};

export default SessionDetailsPage;
